// API Constants - Single source of truth for API config and endpoints

export const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;
const DEFAULT_CLOB_API_URL = 'https://clob.polymarket.com';
const rawClobApiUrl = process.env.NEXT_PUBLIC_CLOB_API_URL || DEFAULT_CLOB_API_URL;

function normalizeClobApiUrl(url: string): string {
  const value = String(url || '').trim();
  if (!value) return DEFAULT_CLOB_API_URL;
  return value.replace(/\/+$/, '');
}

export const CLOB_API_URL = normalizeClobApiUrl(rawClobApiUrl);
export const POLYMARKET_WS_URL = process.env.NEXT_PUBLIC_POLYMARKET_WS_URL;
export const CLOB_WS_URL = process.env.NEXT_PUBLIC_CLOB_WS_URL;
export const POLYMARKET_SPORTS_WS_URL = 'wss://sports-api.polymarket.com';
export const POLYMARKET_RELAYER_URL = process.env.NEXT_PUBLIC_POLYMARKET_RELAYER_URL;
export const POLYMARKET_CHAIN_ID = Number(process.env.NEXT_PUBLIC_POLYMARKET_CHAIN_ID) || 137;
export const ONRAMPER_API_URL = process.env.NEXT_PUBLIC_ONRAMPER_API_URL;
export const ONRAMPER_API_KEY = process.env.NEXT_PUBLIC_ONRAMPER_API_KEY;
export const RELAYER_URL = process.env.NEXT_PUBLIC_RELAYER_URL;
export const CLOB_DATA_API_URL = process.env.NEXT_PUBLIC_CLOB_DATA_API_URL;
export const POLY_BUILDER_CODE = process.env.NEXT_PUBLIC_POLY_BUILDER_CODE || '16';
export const RELAYER_API_KEY = process.env.NEXT_PUBLIC_POLYMARKET_BUILDER_KEY;
export const RELAYER_API_KEY_ADDRESS = process.env.NEXT_PUBLIC_RELAYER_API_KEY_ADDRESS;
export const IPAPI_URL = 'https://ipapi.co/json/';

// Use API_BASE_URL for signing server as the primary source
export const SIGNING_SERVER_URL = `${API_BASE_URL}/api/v1/proxy-wallet/sign`;

export const SKIP_BUILDER_AUTH = process.env.NEXT_PUBLIC_SKIP_BUILDER_AUTH === 'true';
export const API_TIMEOUT = 30000;

export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/api/v1/app-auth/login',
    LOGIN_CREDENTIALS: '/api/v1/app-auth/login',
    LOGOUT: '/api/v1/app-auth/logout',
    STATUS: '/api/v1/app-auth/status',
    REFRESH: '/api/v1/app-auth/refresh',
    WALLET: '/api/v1/wallet',
    PROXY_WALLET: '/api/v1/proxy-wallet',
    PROXY_WALLET_SIGN: '/api/v1/proxy-wallet/sign',
    SET_CREDENTIALS: '/api/v1/auth/set-credentials',
    UPDATE_DISPLAY_NAME: '/api/v1/auth/update-display-name',
  },

  // CLOB (Central Limit Order Book) Credentials
  CLOB: {
    /** Triggers backend to provision CLOB API keys using Privy signer */
    PROVISION_CREDENTIALS: '/api/v1/trading-creds/provision',
    /** Check if CLOB credentials exist for the user */
    CREDENTIALS_STATUS: '/api/v1/trading-creds/status',
    /** Retrieve stored CLOB credentials (decrypted) */
    GET_CREDENTIALS: '/api/v1/trading-creds/credentials',
    /** Persist (upsert) CLOB credentials */
    SET_CREDENTIALS: '/api/v1/trading-creds/credentials',
    /** Delete stored CLOB credentials */
    DELETE_CREDENTIALS: '/api/v1/trading-creds/credentials',
    /** Polymarket CLOB prices history */
    PRICES_HISTORY: `${CLOB_API_URL}/prices-history`,
    TICK_SIZE: `${CLOB_API_URL}/tick-size`,
    BOOK: `${CLOB_API_URL}/book`,
    BOOKS: `${CLOB_API_URL}/books`,
    DATA: {
      ORDERS: `${CLOB_API_URL}/data/orders`,
      TRADES: `${CLOB_API_URL}/data/trades`,
    },
  },

  // CLOB Data (Portfolio value etc)
  CLOB_DATA: {
    VALUE: `${CLOB_DATA_API_URL}/value`,
    OTHER: `${CLOB_DATA_API_URL}/other`,
  },

  // Fiat / Transfers
  FIAT: {
    DEPOSITS: '/api/v1/deposits',
    BRIDGE_DEPOSIT_ADDRESS: '/api/v1/deposits/bridge/deposit-address',
    WALLET_WITHDRAWAL_PREPARE: '/api/v1/withdrawals-v3/wallet/prepare',
    WALLET_WITHDRAWAL_CONFIRM: '/api/v1/withdrawals-v3/wallet/confirm',
    WITHDRAWAL_PREPARE: '/api/v1/withdrawals-v3/bank/prepare',
    WITHDRAWAL_SUBMIT: '/api/v1/withdrawals-v3/bank/submit',
    WITHDRAWAL_CONFIRM: '/api/v1/withdrawals-v3/bank/confirm',
    WITHDRAWAL_CALLBACK: '/api/v1/withdrawals-v3/bank/onramper-callback',
    BRIDGE_PREPARE: '/api/v1/withdrawals-v3/bridge/prepare',
    BRIDGE_SUBMIT: '/api/v1/withdrawals-v3/bridge/submit',
    BRIDGE_STATUS: (id: number) => `/api/v1/withdrawals-v3/bridge/${id}/status`,
    BRIDGE_QUOTE: '/api/v1/withdrawals-v3/bridge/quote',
    BRIDGE_OPTIONS: '/api/v1/withdrawals-v3/bridge/options',
  },

  // Onramper (External)
  ONRAMPER: {
    SUPPORTED: '/supported',
    COUNTRIES: '/supported/countries',
    DEFAULTS_ALL: '/supported/defaults/all',
    PAYMENT_TYPES: '/supported/payment-types',
    QUOTES: (sourceCurrency: string, destinationCurrency: string) =>
      `/quotes/${sourceCurrency}/${destinationCurrency}`,
  },

  // Alchemy (External)
  ALCHEMY: {
    POLYGON_MAINNET: (apiKey: string) => `https://polygon-mainnet.g.alchemy.com/v2/${apiKey}`,
  },

  // Trade
  TRADE: {
    ORDER: '/api/v1/trade/order',
    ORDERS: '/api/v1/trade/orders',
    ORDER_BY_ID: (id: string) => `/api/v1/trade/order/${id}`,
    CANCEL_ALL: '/api/v1/trade/orders/cancel-all',
  },

  // Markets Discovery
  MARKETS_DISCOVERY_CATEGORIES: '/api/v1/markets/discovery/categories',
  MARKETS_DISCOVERY_TAGS: '/api/v1/markets/discovery/tags',
  GLOBAL_SEARCH: '/api/v1/markets/search',

  // Markets Tags
  MARKETS_TAGS_BY_SLUG: (slug: string) => `/api/v1/markets/tags/${slug}`,

  // Leaderboard
  LEADERBOARD: {
    BASE: '/api/v1/leader-board',
    CATEGORIES: '/api/v1/leader-board/categories',
    TIME_PERIODS: '/api/v1/leader-board/time-periods',
    BIGGEST_WINNERS: '/api/v1/leader-board/biggest-winners',
    USER_VALUE: '/api/v1/leader-board/user-value',
    PUBLIC_PROFILE: '/api/v1/leader-board/public-profile',
    SORT_CATEGORIES: (type: string) => `/api/v1/markets/categories/${type}`,
  },

  // Crypto / Finance sidebar filters (counts endpoint)
  CRYPTO_FINANCE_FILTERS: (category: string) =>
    `/api/v1/markets/tags/${category}/${category}/counts`,
  MARKETS_TAGS_TRENDING: '/api/v1/markets/tags/trending',
  MARKETS_TAGS_BREAKING: '/api/v1/markets/tags/breaking',

  // Markets
  MARKETS_BASE: '/api/v1/markets',
  MARKETS_RULE_TRANSLATION: '/api/v1/markets/market-rule-translation',
  MARKETS_BY_ID: (id: string) => `/api/v1/markets/${id}`,
  MARKETS_TRADE: (marketId: string) => `/api/v1/markets/${marketId}/trade`,
  MARKETS_GROK_SUMMARY: '/api/v1/markets/grok/event-summary',
  MARKETS_EVENTS_LIST: '/api/v1/markets/events/list',
  MARKETS_EVENTS_CRYPTO: '/api/v1/markets/events/list',
  MARKETS_EVENT_BY_SLUG: (slug: string) => `/api/v1/markets/events/${slug}`,
  MARKETS_COMMENTS: '/api/v1/markets/comments',
  MARKETS_ACTIVITY: '/api/v1/markets/activity-trades',
  MARKETS_STATS: (address: string) => `/api/v1/markets/stats/${address}`,
  MARKETS_POSITIONS: '/api/v1/markets/positions',
  MARKETS_CLOSED_POSITIONS: '/api/v1/markets/closed-positions',
  MARKETS_PRICES_HISTORY: '/api/v1/markets/prices-history',
  MARKETS_USER_PNL: '/api/v1/markets/user-pnl',
  MARKETS_HOLDERS: '/api/v1/markets/holders',
  PORTFOLIO_CATEGORIES: '/api/v1/portfolio/categories',
  PORTFOLIO_ACTIONS: '/api/v1/portfolio/actions',
  PORTFOLIO_SORT_OPTIONS: '/api/v1/portfolio/sort-options',
  PORTFOLIO_USDC_BALANCE: '/api/usdc-balance',
  PORTFOLIO_DEPOSIT_ADDRESS: '/api/v1/portfolio/deposit-address',
  MARKETS_ACTIVITY_PORTFOLIO: '/api/v1/markets/activity',
  GEOBLOCK: 'https://polymarket.com/api/geoblock',
  GAMMA_EVENT: (eventId: string) => `https://gamma-api.polymarket.com/events/${eventId}`,

  // Events
  EVENTS: '/events',

  // Sports
  MARKETS_SPORTS_SIDEBAR: '/api/v1/markets/sports?type=all',
  MARKETS_SPORTS_FUTURE_CATEGORIES: '/api/v1/markets/sports/future-categories',
  MARKETS_SPORTS_PRICES_HISTORY: '/api/v1/markets/prices-history',

  // WebSocket
  MARKET_WS_ENDPOINT: '/ws/market',
  SPORTS_WS_ENDPOINT: '/ws',

  //Static Pages
  STATIC_PAGES: {
    TERMS: (id: string | number) => `/api/v1/static-pages/${id}`,
  },
  CONTACT: 'api/v1/contact-us',

  // Bookmarks
  BOOKMARKS: {
    LIST: '/api/v1/bookmarks/bookmark',
    TOGGLE: '/api/v1/bookmarks/toggle-bookmark',
  },

  // Notifications
  NOTIFICATIONS: {
    LIST: '/api/v1/notifications',
    UNREAD_COUNT: '/api/v1/notifications/unread-count',
    MARK_READ: '/api/v1/notifications/mark-read',
  },

  // Price alerts
  ALERTS: {
    LIST: '/api/v1/alerts',
    CREATE: '/api/v1/alerts',
    DELETE: (id: number | string) => `/api/v1/alerts/${id}`,
  },

  // Referral
  REFERRAL: {
    STATUS: '/api/v1/referral/status',
    HISTORY: '/api/v1/referral/history',
    VALIDATE: (code: string) => `/api/v1/referral/validate/${code}`,
    APPLY: '/api/v1/referral/apply',
    CONFIG: '/api/v1/admin/referral-config',
    PROMOTE: (id: string) => `/api/v1/admin/users/${id}/ambassador`,
    BATCH_INVITE: '/api/v1/admin/ambassadors/batch-invite',
    PAYOUTS_EXECUTE: '/api/v1/admin/referral/payouts/execute',
  },
} as const;

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const;

export const PAGINATION = {
  DEFAULT_LIMIT: 20,
  DEFAULT_PAGE: 1,
  DEFAULT_COMMENT_LIMIT: 5,
  DEFAULT_HOLDERS_LIMIT: 30,
} as const;

export const EXTERNAL_LINKS = {
  POLYGONSCAN_TX: (hash: string) => `https://polygonscan.com/tx/${hash}`,
  POLYMARKET_EVENT: (slug: string) => `https://polymarket.com/event/${slug}`,
} as const;

export const POLYGON_CHAIN_ID = Number(process.env.NEXT_PUBLIC_POLYGON_CHAIN_ID) || 137;
export const FLAGS_BASE_URL = 'https://hatscripts.github.io/circle-flags/flags';
